import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';

const STEPS = ['Basic Info', 'Account Setup', 'Done'];

export default function RegisterPage() {
  const [step, setStep] = useState(0);
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const { register: registerUser, loading } = useAuthStore();
  const navigate = useNavigate();
  const gender = watch('gender');

  const onSubmit = async (data) => {
    if (step < 1) { setStep(s => s + 1); return; }
    try {
      await registerUser({
        mobile:    data.mobile,
        email:     data.email,
        password:  data.password,
        gender:    data.gender,
        firstName: data.firstName,
        lastName:  data.lastName,
      });
      toast.success('Registration successful! Welcome! 🙏');
      navigate('/profile/setup');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/cross-icon.png" alt="Cross" className="w-10 h-10 rounded object-cover mx-auto" />
          <h1 className="font-heading text-3xl font-bold text-white mt-2">Create Account</h1>
          <p className="text-primary-100 mt-1">Join the Holyweds Community</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors ${
                i <= step ? 'bg-gold text-white' : 'bg-white/20 text-primary-100'
              }`}>{i + 1}</div>
              {i < STEPS.length - 1 && <div className={`h-px w-8 ${i < step ? 'bg-gold' : 'bg-white/20'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {step === 0 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">I am looking for a...</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['male','female'].map(g => (
                      <label key={g} className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        gender === g ? 'border-primary bg-primary-50 text-primary' : 'border-gray-200 hover:border-primary-300'
                      }`}>
                        <input type="radio" value={g} {...register('gender', { required: true })} className="sr-only" />
                        <span className="text-lg">{g === 'male' ? '👨' : '👩'}</span>
                        <span className="font-medium capitalize">{g === 'male' ? 'Groom' : 'Bride'}</span>
                      </label>
                    ))}
                  </div>
                  {errors.gender && <p className="text-red-500 text-xs mt-1">Please select</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input className="input-field" placeholder="First name"
                      {...register('firstName', { required: 'Required' })} />
                    {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input className="input-field" placeholder="Last name"
                      {...register('lastName', { required: 'Required' })} />
                    {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
                  </div>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <input type="tel" className="input-field" placeholder="10-digit mobile"
                    {...register('mobile', {
                      required: 'Mobile number required',
                      pattern: { value: /^[6-9]\d{9}$/, message: 'Enter valid Indian number' }
                    })} />
                  {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                  <input type="email" className="input-field" placeholder="your@email.com"
                    {...register('email', {
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' }
                    })} />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input type="password" className="input-field" placeholder="Min. 6 characters"
                    {...register('password', {
                      required: 'Password required',
                      minLength: { value: 6, message: 'At least 6 characters' }
                    })} />
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                <p className="text-xs text-gray-400 leading-relaxed">
                  By registering, you agree to our Terms of Service and Privacy Policy. Your first 30 days are free.
                </p>
              </>
            )}

            <div className="flex gap-3 pt-2">
              {step > 0 && (
                <button type="button" onClick={() => setStep(s => s - 1)}
                  className="btn-outline flex-1 py-2.5">Back</button>
              )}
              <button type="submit" disabled={loading}
                className="btn-primary flex-1 py-2.5 disabled:opacity-50">
                {step === 1 ? (loading ? 'Creating...' : 'Create Account') : 'Next →'}
              </button>
            </div>
          </form>

          <div className="cross-divider my-5">already have an account</div>
          <Link to="/login" className="block text-center text-primary text-sm font-semibold hover:underline">Sign In</Link>
        </div>
      </div>
    </div>
  );
}
